import React, { FC, useState } from 'react';
import { useData } from '@hooks/useData';
import { GetMyStoresOrderDetailSuccessResponse } from '@services/useGetMyStoresOrderDetail';
import { OrderDetailManagementTable } from '../../OrderDetailManagementTable';
import { useTranslation } from 'react-i18next';
import { CancelLockerCompartmentReservationModal } from '../../modals/CancelLockerCompartmentBookingModal';
import { Typography } from '@components/atoms/Typography';

type Delivery = NonNullable<GetMyStoresOrderDetailSuccessResponse['data']>['delivery'];
type DeliveryRow = { id: string; name?: string; address?: string; products?: string[]; isDigital?: boolean };

interface DeliveryTableProps {
    orderUuid: string;
    storeUuid: string;
}

export const DeliveryTable: FC<DeliveryTableProps> = ({ orderUuid, storeUuid }) => {
    const { myStoresOrderDetail, isLoadingMyStoresOrderDetail, isLoadingCancelLockerCompartmentReservation } = useData();
    const { t } = useTranslation();

    const translations = {
        address: t('manage-order-detail-delivery-table-component.header.address'),
        booking: t('manage-order-detail-delivery-table-component.header.booking'),
        products: t('manage-order-detail-delivery-table-component.header.products'),
        noBookings: t('manage-order-detail-delivery-table-component.header.no-bookings'),
        digitalDelivery: t('manage-order-detail-delivery-table-component.digital-delivery'),
    };

    // if null, hide modal
    const [cancelBookingModal, setCancelBookingModal] = useState<string | null>(null);

    const delivery = !isLoadingMyStoresOrderDetail ? myStoresOrderDetail?.data?.delivery || null : null;

    const deliveries = getDeliveries(delivery, translations.digitalDelivery);

    const renderDeliveryCell = (item: DeliveryRow) => {
        if (item.isDigital) {
            return <p style={{ whiteSpace: 'pre-line' }}>{item.address}</p>;
        }

        return (
            <>
                {item.name && (
                    <>
                        <b>{item.name}</b>
                        <br />
                    </>
                )}
                {item.address && <p style={{ whiteSpace: 'pre-line' }}>{item.address}</p>}
            </>
        );
    };

    const showConfirmCancelDeliveryModal = (deliveryId: string) => {
        setCancelBookingModal(deliveryId);
    };

    return (
        <>
            {delivery?.locker ? (
                <>
                    <OrderDetailManagementTable
                        withActions={false}
                        header={[{ text: translations.address }]}
                        content={[
                            {
                                cells: [
                                    {
                                        content: (
                                            <>
                                                <Typography classes="mb-3" fontSize="16">
                                                    <>{delivery.locker?.name || ''}</>
                                                </Typography>
                                                <Typography classes="mb-1" fontSize="14">
                                                    {delivery.locker?.address}
                                                </Typography>
                                                <Typography classes="mb-1" fontSize="14">
                                                    {delivery.locker?.postalCode}
                                                </Typography>
                                                <Typography classes="mb-1" fontSize="14">
                                                    {delivery.locker?.location}
                                                </Typography>
                                            </>
                                        ),
                                    },
                                ],
                                options: [],
                            },
                        ]}
                    />
                    <OrderDetailManagementTable
                        withActions={true}
                        header={[{ text: translations.booking }, { text: translations.products, hideOnMobile: true }]}
                        content={
                            deliveries.length > 0
                                ? deliveries.map((item) => {
                                      return {
                                          cells: [
                                              {
                                                  content: <b>{item.name || ''}</b>,
                                              },
                                              {
                                                  content: (
                                                      <>
                                                          {item.products?.map((product) => (
                                                              <p style={{ whiteSpace: 'pre-line' }}>{product}</p>
                                                          ))}
                                                      </>
                                                  ),
                                              },
                                          ],
                                          options: [
                                              {
                                                  icon: 'fa-light fa-xmark',
                                                  action: () =>
                                                      !isLoadingCancelLockerCompartmentReservation &&
                                                      showConfirmCancelDeliveryModal(item.id),
                                              },
                                          ],
                                      };
                                  })
                                : [
                                      {
                                          cells: [
                                              {
                                                  content: (
                                                      <Typography classes="mb-1" fontSize="14">
                                                          {translations.noBookings}
                                                      </Typography>
                                                  ),
                                              },
                                          ],
                                          options: [],
                                      },
                                  ]
                        }
                    />
                    <CancelLockerCompartmentReservationModal
                        orderUuid={orderUuid}
                        storeUuid={storeUuid}
                        reservationId={cancelBookingModal}
                        show={cancelBookingModal !== null}
                        onClose={() => setCancelBookingModal(null)}
                    />
                </>
            ) : (
                <OrderDetailManagementTable
                    withActions={false}
                    header={[{ text: translations.address }]}
                    content={deliveries.map((item) => {
                        return {
                            cells: [
                                {
                                    content: renderDeliveryCell(item),
                                },
                            ],
                            options: [],
                        };
                    })}
                />
            )}
        </>
    );
};

const getDeliveries: (delivery: Delivery | null, digitalDeliveryFallback: string) => DeliveryRow[] = (
    delivery,
    digitalDeliveryFallback
) => {
    if (delivery?.shipToPickupSpot) {
        return [
            {
                id: '1',
                name: delivery.shipToPickupSpot.name,
                address: delivery.shipToPickupSpot.address || '',
            },
        ];
    }

    if (delivery?.pickupAtStore) {
        return delivery.pickupAtStore.map((item, index) => ({
            id: `${index}`,
            name: item.name,
            address: item.address || '',
        }));
    }

    if (delivery?.digitalDelivery) {
        const shippingAddress = delivery?.shipToAddress;
        const digitalLabel = typeof shippingAddress === 'string' ? shippingAddress : '';

        return [
            {
                id: '1',
                address: digitalLabel || digitalDeliveryFallback,
                isDigital: true,
            },
        ];
    }

    if (delivery?.shipToAddress && typeof delivery.shipToAddress === 'object' && delivery.shipToAddress !== null) {
        const shippingAddress = delivery.shipToAddress as {
            recipient_first_name?: string;
            recipient_last_name?: string;
            fiscal_number?: string;
            address_line1?: string;
            address_line2?: string;
            postal_code?: string;
            municipality?: string;
            region?: { name?: string };
        };
        const shippingAddressText = [
            `${shippingAddress.recipient_first_name || ''} ${shippingAddress.recipient_last_name || ''}`.trim(),
            `NIF: ${shippingAddress.fiscal_number || '-'}`,
            shippingAddress.address_line1 || '',
            shippingAddress.address_line2 || '',
            shippingAddress.postal_code || '',
            shippingAddress.municipality || '',
            shippingAddress.region?.name || '',
        ]
            .filter((line) => line !== '')
            .join('\n');

        return [
            {
                id: '1',
                address: shippingAddressText,
            },
        ];
    }

    if (delivery?.locker) {
        return (delivery.lockerReservations || []).map((reservation) => ({
            id: reservation.reservation_id || '',
            name: reservation.reservation_number || '',
            products: reservation.orderItems?.map((item) => {
                return item.quantity + ' x ' + item.name + (item.attributes ? ', ' + item.attributes?.join(', ') : '');
            }),
        }));
    }

    return [];
};
